import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { OfflineAlert } from "@/components/ui/offline-alert";
import { AuthProvider } from "@/contexts/AuthContext";

import { GtagRouteChangeTracker } from "@/components/analytics/GtagRouteChangeTracker";
import { PosthogRouteChangeTracker } from "@/components/analytics/PosthogRouteChangeTracker";

import ProtectedRoute from "@/components/ProtectedRoute";
import { ProtectedRouteWithProfileCheck } from "@/components/ProtectedRouteWithProfileCheck";
import Index from "@/pages/Index";
import About from "@/pages/About";
import Login from "@/pages/Login";
import SignUp from "@/pages/SignUp";
import CompleteProfile from "@/pages/CompleteProfile";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import NotFound from "@/pages/NotFound";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Payroll from "@/pages/services/Payroll";
import Benefits from "@/pages/services/Benefits";
import HRCompliance from "@/pages/services/HRCompliance";
import WorkersComp from "@/pages/services/WorkersComp";
import RiskManagement from "@/pages/services/RiskManagement";
import Application from "@/pages/Application";
import Success from "@/pages/Success";
import Partnerships from "@/pages/Partnerships";
import ForBrokers from "@/pages/ForBrokers";
import ForPEOs from "@/pages/ForPEOs";
import PEOApply from "@/pages/peo/Apply";
import PEOVettingProcess from "@/pages/PEOVettingProcess";
import Technology from "@/pages/Technology";
import HowItWorks from "@/pages/HowItWorks";
import SitemapPage from "@/pages/Sitemap";
import { BestPEOProviders } from "@/pages/BestPEOProviders";
import { NortheastRegion } from "@/pages/regions/NortheastRegion";
import { SouthRegion } from "@/pages/regions/SouthRegion";
import { WestRegion } from "@/pages/regions/WestRegion";
import { MidwestRegion } from "@/pages/regions/MidwestRegion";
import { StatesDirectory } from "@/pages/StatesDirectory";
import { StateRouter } from "@/components/StateRouter";
import { PEOSelectionGuide } from "@/pages/PEOSelectionGuide";

// Admin pages
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminUsers from "@/pages/admin/Users";
import AdminApplications from "@/pages/admin/Applications";
import AdminPEOManagement from "@/pages/admin/PEOManagement";
import AdminQuoteManagement from "@/pages/admin/QuoteManagement";
import AdminClients from "@/pages/admin/Clients";
import AdminPEOVerifications from "@/pages/admin/PEOVerifications";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminSettings from "@/pages/admin/Settings";
import { AuthDebugPage } from "@/pages/admin/AuthDebugPage";


// PEO pages
import PEODashboard from "@/pages/peo/Dashboard";
import RestrictedAccess from "@/pages/peo/RestrictedAccess";

// SEO-focused pages
import PEOShoppingProblems from "@/pages/PEOShoppingProblems";
import PEOShoppingGuide from "@/pages/PEOShoppingGuide";

// Industry pages
import Industries from "@/pages/Industries";
import HealthcarePEO from "@/pages/industries/Healthcare";
import ConstructionPEO from "@/pages/industries/Construction";
import ProfessionalServicesPEO from "@/pages/industries/ProfessionalServices";
import TechnologyPEO from "@/pages/industries/Technology";
import ManufacturingPEO from "@/pages/industries/Manufacturing";
import RestaurantPEO from "@/pages/industries/Restaurant";
import RetailPEO from "@/pages/industries/Retail";
import NonProfitPEO from "@/pages/industries/NonProfit";

// City pages
import LosAngelesPage from "@/pages/cities/LosAngeles";
import SanFranciscoPage from "@/pages/cities/SanFrancisco";
import SanDiegoPage from "@/pages/cities/SanDiego";
import HoustonPage from "@/pages/cities/Houston";
import DallasPage from "@/pages/cities/Dallas";
import AustinPage from "@/pages/cities/Austin";
import MiamiPage from "@/pages/cities/Miami";
import NewYorkPage from "@/pages/cities/NewYork";
import ChicagoPage from "@/pages/cities/Chicago";
import PhoenixPage from "@/pages/cities/Phoenix";
import PhiladelphiaPage from "@/pages/cities/Philadelphia";
import AtlantaPage from "@/pages/cities/Atlanta";

const queryClient = new QueryClient();

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        
          <AuthProvider>
            <Router>
              <GtagRouteChangeTracker />
              <PosthogRouteChangeTracker />
            <div className="min-h-screen bg-background">
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/complete-profile" element={<CompleteProfile />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                
                {/* Protected Dashboard Route */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRouteWithProfileCheck>
                      <Dashboard />
                    </ProtectedRouteWithProfileCheck>
                  } 
                />
                
                {/* PEO Dashboard Routes */}
                <Route 
                  path="/peo/dashboard" 
                  element={
                    <ProtectedRoute requiredRole="peo">
                      <PEODashboard />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/peo/restricted" 
                  element={
                    <ProtectedRoute requiredRole="peo">
                      <RestrictedAccess />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Protected Application Route */}
                <Route 
                  path="/application" 
                  element={
                    <ProtectedRoute>
                      <Application />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Protected Success Route */}
                <Route 
                  path="/success" 
                  element={
                    <ProtectedRoute>
                      <Success />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Admin Routes - Super Admin Only */}
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminUsers />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/applications" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminApplications />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/peo-management" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminPEOManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/quote-management" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminQuoteManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/clients" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminClients />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/peo-verifications" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminPEOVerifications />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/analytics" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminAnalytics />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/settings" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminSettings />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/auth-debug" 
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AuthDebugPage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* SEO-focused pages */}
                <Route path="/peo-shopping-problems" element={<PEOShoppingProblems />} />
                <Route path="/peo-shopping-guide" element={<PEOShoppingGuide />} />
                
                {/* Industry pages */}
                <Route path="/industries" element={<Industries />} />
                <Route path="/industries/healthcare" element={<HealthcarePEO />} />
                <Route path="/industries/construction" element={<ConstructionPEO />} />
                <Route path="/industries/professional-services" element={<ProfessionalServicesPEO />} />
                <Route path="/industries/technology" element={<TechnologyPEO />} />
                <Route path="/industries/manufacturing" element={<ManufacturingPEO />} />
                <Route path="/industries/restaurant" element={<RestaurantPEO />} />
                <Route path="/industries/retail" element={<RetailPEO />} />
                <Route path="/industries/non-profit" element={<NonProfitPEO />} />

                {/* City pages */}
                <Route path="/cities/los-angeles-ca" element={<LosAngelesPage />} />
                <Route path="/cities/san-francisco-ca" element={<SanFranciscoPage />} />
                <Route path="/cities/san-diego-ca" element={<SanDiegoPage />} />
                <Route path="/cities/houston-tx" element={<HoustonPage />} />
                <Route path="/cities/dallas-tx" element={<DallasPage />} />
                <Route path="/cities/austin-tx" element={<AustinPage />} />
                <Route path="/cities/miami-fl" element={<MiamiPage />} />
                <Route path="/cities/new-york-ny" element={<NewYorkPage />} />
                <Route path="/cities/chicago-il" element={<ChicagoPage />} />
                <Route path="/cities/phoenix-az" element={<PhoenixPage />} />
                <Route path="/cities/philadelphia-pa" element={<PhiladelphiaPage />} />
                <Route path="/cities/atlanta-ga" element={<AtlantaPage />} />

                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/services/payroll" element={<Payroll />} />
                <Route path="/services/benefits" element={<Benefits />} />
                <Route path="/services/hr-compliance" element={<HRCompliance />} />
                <Route path="/services/workers-comp" element={<WorkersComp />} />
                <Route path="/services/risk-management" element={<RiskManagement />} />
                <Route path="/partnerships" element={<Partnerships />} />
                <Route path="/for-brokers" element={<ForBrokers />} />
                <Route path="/for-peos" element={<ForPEOs />} />
                <Route path="/peo/apply" element={<PEOApply />} />
                <Route path="/peo-vetting-process" element={<PEOVettingProcess />} />
                <Route path="/technology" element={<Technology />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/best-peo-providers" element={<BestPEOProviders />} />
                <Route path="/peo-selection-guide" element={<PEOSelectionGuide />} />
                
                {/* Regional Pages */}
                <Route path="/regions/northeast" element={<NortheastRegion />} />
                <Route path="/regions/south" element={<SouthRegion />} />
                <Route path="/regions/west" element={<WestRegion />} />
                <Route path="/regions/midwest" element={<MidwestRegion />} />
                
                <Route path="/states" element={<StatesDirectory />} />
                <Route path="/states/:stateSlug" element={<StateRouter />} />
                
                <Route path="/sitemap" element={<SitemapPage />} />
                
                <Route path="*" element={<NotFound />} />
              </Routes>
              <Toaster />
              <OfflineAlert />
              
            </div>
          </Router>
        </AuthProvider>
      
    </QueryClientProvider>
  </HelmetProvider>
  );
}

export default App;
